import React, { Suspense } from "react";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import { page_routes } from "./helper/page_routes";
import { ToastContainer } from "react-toastify";
import UserProvider from "./context/userProvider";
import { LoadingProvider } from "./context/loadingProvider";
import PersistUserLoginLayout from "./layouts/persistUserLoginLayout";
import AxiosProvider from "./context/axiosProvider";
import PageLoader from "./components/pageLoader";
import RouteTracker from "./components/common/RouteTracker";
import Moment from "react-moment";
import moment from "moment-timezone";

const ProtectedRoutesLayout = React.lazy(
  () => import("./layouts/protectedRoutesLayout")
);
const UnprotectedRoutesLayout = React.lazy(
  () => import("./layouts/unprotectedRoutesLayout")
);
const DashboardLayout = React.lazy(() => import("./layouts/dashboardLayout"));
const Dashboard = React.lazy(() => import("./pages/dashboard"));
const Challenges = React.lazy(() => import("./pages/challenges"));
const Recognition = React.lazy(() => import("./pages/recognition"));
const Awards = React.lazy(() => import("./pages/awards"));
const Rewards = React.lazy(() => import("./pages/rewards"));
const RewardReports = React.lazy(() => import("./pages/rewards/rewardReports"));
const RewardCategories = React.lazy(
  () => import("./pages/rewards/category/index")
);
const RewardSubCategories = React.lazy(
  () => import("./pages/rewards/subCategory/index")
);
const Redemptions = React.lazy(() => import("./pages/redemptions"));
const Profile = React.lazy(() => import("./pages/profile"));
const MyDetail = React.lazy(() => import("./pages/myDetail"));
const MoodHistoryPage = React.lazy(() => import("./pages/moodHistory"));
const ProtectedAdminRoutesLayout = React.lazy(
  () => import("./layouts/protectedAdminRoutesLayout")
);
const AdminUsers = React.lazy(() => import("./pages/admin/users"));
const AdminDashboard = React.lazy(() => import("./pages/admin/dashboard"));
const AdminEditUser = React.lazy(() => import("./pages/admin/users/editUser"));
const AdminSkills = React.lazy(() => import("./pages/admin/skills"));
const AdminEvents = React.lazy(() => import("./pages/admin/events"));
const AdminVendors = React.lazy(() => import("./pages/admin/vendors"));
const PageNotFound = React.lazy(() => import("./pages/pageNotFound"));
const InsufficientPermissionPage = React.lazy(
  () => import("./pages/insufficientPermissionPage")
);
const Surveys = React.lazy(() => import("./components/surveys"));
const AddOrEditSurvey = React.lazy(
  () => import("./components/surveys/AddOrEditSurvey")
);
const SubmitSurvey = React.lazy(
  () => import("./components/surveys/SubmitSurvey")
);
const UserSurveys = React.lazy(() => import("./pages/userSurveys"));
const SurveyReport = React.lazy(
  () => import("./components/surveys/SurveyReport")
);
const SurveyAnalytics = React.lazy(
  () => import("./components/surveys/SurveyAnalytics")
);
const WellbeingSurveysPage = React.lazy(
  () => import("./pages/wellbeingSurveys")
);
const WellbeingResourcesPage = React.lazy(
  () => import("./pages/wellbeingResources")
);
const MyToolsPage = React.lazy(() => import("./pages/myTools"));
const MyTeamsPage = React.lazy(() => import("./pages/my-teams/MyTeams"));
const TeamFeed = React.lazy(() => import("./pages/team-feed"));
const WellbeingFeedbackPage = React.lazy(
  () => import("./pages/wellbeingFeedback")
);
const AdminWellbeingFeedbackPage = React.lazy(
  () => import("./pages/admin/wellbeingFeedback")
);
const WellnessHomePage = React.lazy(
  () => import("./pages/wellnessHome")
);
const WellnessEventsPage = React.lazy(
  () => import("./pages/wellnessEvents")
);
const UserWellnessProgramsPage = React.lazy(
  () => import("./pages/wellnessPrograms/index")
);
const UserWellnessProgramDetailPage = React.lazy(
  () => import("./pages/wellnessPrograms/detail")
);
const UserWellnessChallengesPage = React.lazy(
  () => import("./pages/wellnessChallenges/index")
);
const UserWellnessChallengeDetailPage = React.lazy(
  () => import("./pages/wellnessChallenges/detail")
);
const AdminWellnessDashboard = React.lazy(
  () => import("./pages/admin/wellnessDashboard")
);
const AdminGoalManagementPage = React.lazy(
  () => import("./pages/admin/goalManagement")
);
const AdminWellnessProgramsPage = React.lazy(
  () => import("./pages/admin/wellnessPrograms")
);
const AdminCreateWellnessProgramPage = React.lazy(
  () => import("./pages/admin/wellnessPrograms/createProgram")
);
const AdminWellnessProgramDetailPage = React.lazy(
  () => import("./pages/admin/wellnessPrograms/detailProgram")
);
const AdminEditWellnessProgramPage = React.lazy(
  () => import("./pages/admin/wellnessPrograms/editProgram")
);
const AdminCohortAnalyticsPage = React.lazy(
  () => import("./pages/admin/wellnessPrograms/cohortAnalytics")
);
const AdminWellnessChallengesPage = React.lazy(
  () => import("./pages/admin/wellnessChallenges/index")
);
const AdminWellnessChallengeDetailPage = React.lazy(
  () => import("./pages/admin/wellnessChallenges/detail")
);
const AdminCreateWellnessChallengePage = React.lazy(
  () => import("./pages/admin/wellnessChallenges/create")
);
const AdminEditWellnessChallengePage = React.lazy(
  () => import("./pages/admin/wellnessChallenges/editChallenge")
);
const TeamsPage = React.lazy(() => import("./pages/admin/teams"));
const AdminNavigationSettingsPage = React.lazy(
  () => import("./pages/admin/navigation")
);
const AdminWellnessCorrelationPage = React.lazy(
  () => import("./pages/admin/wellnessCorrelation")
);
const AdminWellnessSupportRequestsPage = React.lazy(
  () => import("./pages/admin/wellnessSupportRequests")
);
const MyWellnessSupportRequestsPage = React.lazy(
  () => import("./pages/myWellnessSupportRequests")
);
// const Login = React.lazy(()=>import("./pages/login"));

Moment.globalMoment = moment;
Moment.globalTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
Moment.globalLocale = Intl.DateTimeFormat().resolvedOptions().locale;
Moment.globalLocal = true;

/*
 * App Component Containing Providers, Routes & Toast Container
 */

function App() {
  return (
    <>
      <UserProvider>
        <LoadingProvider>
          <BrowserRouter basename={page_routes.dashboard}>
            <AxiosProvider>
              <RouteTracker />
              <Routes>
                <Route element={<PersistUserLoginLayout />}>
                  <Route element={<ProtectedRoutesLayout />}>
                    <Route
                      path={page_routes.dashboard}
                      element={<DashboardLayout />}
                    >
                      <Route
                        path={page_routes.dashboard}
                        element={<Dashboard />}
                      />{" "}
                      {/* 👈 Renders Dashboard Screen at / */}
                      <Route
                        path={page_routes.challenges}
                        element={<Challenges />}
                      />{" "}
                      {/* 👈 Renders Challenges Screen at / */}
                      <Route
                        path={page_routes.recognition}
                        element={<Recognition />}
                      />{" "}
                      {/* 👈 Renders Recognition Screen at / */}
                      <Route
                        path={page_routes.rewards}
                        element={<Rewards />}
                      />{" "}
                      {/* 👈 Renders Rewards Screen at / */}
                      <Route
                        path={page_routes.redemtions}
                        element={<Redemptions />}
                      />{" "}
                      {/* 👈 Renders Redemptions Screen at / */}
                      <Route
                        path={page_routes.awards}
                        element={<Awards />}
                      />{" "}
                      {/* 👈 Renders Rewards Screen at / */}
                      <Route
                        path={page_routes.profile}
                        element={<Profile />}
                      />{" "}
                      {/* 👈 Renders Challenges Screen at / */}
                      <Route
                        path={page_routes.myDetail}
                        element={<MyDetail />}
                      />{" "}
                      {/* 👈 Renders Challenges Screen at / */}
                      <Route
                        path={page_routes.moodHistory}
                        element={<MoodHistoryPage />}
                      />{" "}
                      {/* 👈 Renders Mood History Screen at /mood-history */}
                      <Route
                        path={page_routes.submit_survey + ":id"}
                        element={<SubmitSurvey />}
                      />
                      <Route
                        path={page_routes.user_surveys}
                        element={<UserSurveys />}
                      />
                      <Route
                        path={page_routes.wellbeing_resources}
                        element={<WellbeingResourcesPage />}
                      />{" "}
                      {/* 👈 Renders Resource Library Screen */}
                      <Route
                        path={page_routes.my_tools}
                        element={<MyToolsPage />}
                      />{" "}
                      {/* 👈 Renders My Tools Screen */}
                      <Route
                        path={page_routes.my_teams}
                        element={<MyTeamsPage />}
                      />
                      {/* 👈 Renders My Teams Screen */}
                      <Route
                        path={page_routes.team_feed}
                        element={<TeamFeed />}
                      />
                      {/* 👈 Renders Team Feed Screen */}
                      <Route
                        path={page_routes.wellbeing_feedback}
                        element={<WellbeingFeedbackPage />}
                      />{" "}
                      {/* 👈 Renders Feedback Submission Screen */}
                      <Route
                        path={page_routes.wellness_home}
                        element={<WellnessHomePage />}
                      />{" "}
                      {/* 👈 Renders Employee Wellness Dashboard */}
                      <Route
                        path={page_routes.wellness_events}
                        element={<WellnessEventsPage />}
                      />{" "}
                      <Route
                        path={page_routes.wellness_programs}
                        element={<UserWellnessProgramsPage />}
                      />{" "}
                      <Route
                        path={page_routes.wellness_program_detail}
                        element={<UserWellnessProgramDetailPage />}
                      />{" "}
                      <Route
                        path={page_routes.wellness_challenges}
                        element={<UserWellnessChallengesPage />}
                      />{" "}
                      <Route
                        path={page_routes.wellness_challenge_detail}
                        element={<UserWellnessChallengeDetailPage />}
                      />{" "}
                      <Route
                        path={page_routes.my_wellness_support_requests}
                        element={<MyWellnessSupportRequestsPage />}
                      />{" "}
                      <Route
                        path={page_routes.dashboard}
                        element={<ProtectedAdminRoutesLayout />}
                      >
                        <Route
                          path={page_routes.admin_dashboard}
                          element={<AdminDashboard />}
                        />
                        <Route
                          path={page_routes.admin}
                          element={<AdminUsers />}
                        />
                        <Route
                          path={page_routes.admin_edit_user + ":userId"}
                          element={<AdminEditUser />}
                        />
                        <Route
                          path={page_routes.admin_skills}
                          element={<AdminSkills />}
                        />
                        <Route
                          path={page_routes.admin_events}
                          element={<AdminEvents />}
                        />
                        <Route
                          path={page_routes.admin_vendors}
                          element={<AdminVendors />}
                        />
                        <Route
                          path={page_routes.admin_teams}
                          element={<TeamsPage />}
                        />
                        <Route
                          path={page_routes.reward_categories}
                          element={<RewardCategories />}
                        />{" "}
                        {/* 👈 Renders Rewards Screen at / */}
                        <Route
                          path={page_routes.reward_sub_categories}
                          element={<RewardSubCategories />}
                        />{" "}
                        {/* 👈 Renders Rewards Screen at / */}
                        <Route
                          path={page_routes.wellbeing_surveys}
                          element={<WellbeingSurveysPage />}
                        />{" "}
                        <Route
                          path={page_routes.admin_surveys}
                          element={<Surveys />}
                        />
                        <Route
                          path={page_routes.add_surveys}
                          element={<AddOrEditSurvey action="ADD" />}
                        />
                        <Route
                          path={page_routes.edit_surveys + ":id"}
                          element={<AddOrEditSurvey action="EDIT" />}
                        />
                        <Route
                          path={page_routes.survey_report}
                          element={<SurveyReport />}
                        />
                        <Route
                          path={page_routes.survey_report_analytics}
                          element={<SurveyAnalytics />}
                        />
                        <Route
                          path={page_routes.admin_wellbeing_feedback}
                          element={<AdminWellbeingFeedbackPage />}
                        />{" "}
                        <Route
                          path={page_routes.admin_wellness_dashboard}
                          element={<AdminWellnessDashboard />}
                        />
                        <Route
                          path={page_routes.admin_goal_management}
                          element={<AdminGoalManagementPage />}
                        />
                        <Route
                          path={page_routes.admin_wellness_programs}
                          element={<AdminWellnessProgramsPage />}
                        />
                        <Route
                          path={page_routes.admin_wellness_challenges}
                          element={<AdminWellnessChallengesPage />}
                        />
                        <Route
                          path={page_routes.admin_wellness_challenge_create}
                          element={<AdminCreateWellnessChallengePage />}
                        />
                        <Route
                          path={page_routes.admin_wellness_challenge_detail}
                          element={<AdminWellnessChallengeDetailPage />}
                        />
                        <Route
                          path={page_routes.admin_wellness_challenge_edit}
                          element={<AdminEditWellnessChallengePage />}
                        />
                        <Route
                          path={page_routes.admin_wellness_program_create}
                          element={<AdminCreateWellnessProgramPage />}
                        />
                        <Route
                          path={page_routes.admin_wellness_program_detail}
                          element={<AdminWellnessProgramDetailPage />}
                        />
                        <Route
                          path={page_routes.admin_wellness_program_edit}
                          element={<AdminEditWellnessProgramPage />}
                        />
                        <Route
                          path={page_routes.admin_wellness_cohort_analytics}
                          element={<AdminCohortAnalyticsPage />}
                        />
                        <Route
                          path={page_routes.admin_wellness_correlation}
                          element={<AdminWellnessCorrelationPage />}
                        />
                        <Route
                          path={page_routes.admin_wellness_support_requests}
                          element={<AdminWellnessSupportRequestsPage />}
                        />
                        <Route
                          path={page_routes.admin_navigation}
                          element={<AdminNavigationSettingsPage />}
                        />
                      </Route>
                      <Route
                        path={page_routes.rewardReports}
                        element={<RewardReports />}
                      />{" "}
                      {/* 👈 Renders Rewards Reports at / */}
                    </Route>
                  </Route>

                  <Route element={<UnprotectedRoutesLayout />}>
                    {/* <Route path={page_routes.login} element={<Login />} /> 👈 Renders Login Screen at /login */}
                    <Route
                      path={page_routes.login}
                      Component={() => {
                        window.location.href =
                          process.env.REACT_APP_CURRENT_ENV === "production"
                            ? "https://portal.maximumaccountability.net/login?returnURL=https://ee.maximumaccountability.net"
                            : `https://portal-staging.maximumaccountability.net/login?returnURL=${window.location.origin}`;
                        return null;
                      }}
                    />{" "}
                    👈 Renders Login Screen at /login
                  </Route>
                </Route>
                <Route
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <Outlet />
                    </Suspense>
                  }
                >
                  <Route
                    path={page_routes.insufficientPermission}
                    element={<InsufficientPermissionPage />}
                  />{" "}
                  {/* 👈 Renders Page Not Found Screen */}
                  <Route path="*" element={<PageNotFound />} />{" "}
                  {/* 👈 Renders Page Not Found Screen */}
                </Route>
              </Routes>
            </AxiosProvider>
          </BrowserRouter>
        </LoadingProvider>
      </UserProvider>
      <ToastContainer />
    </>
  );
}

export default App;
